import { distributedCategoricalColor, type Calendar } from "@app/core";
import { Button, Eye, EyeOff, Star, Trash2 } from "@app/ui";
import { useState } from "react";

import { orderByIds } from "../../lib/apply-order.js";
import { Reorder } from "../../lib/motion/index.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { useI18n } from "../../lib/useI18n.js";
import { useCalendarsStore, useSettingsStore, useTasksStore, useThemeStore } from "../../stores/index.js";
import { CAL_PALETTE_OFFSET } from "../entity-colors.js";
import { AddEntityRow } from "./AddEntityRow.js";
import { DeleteEntityDialog } from "./DeleteEntityDialog.js";
import { EntityRow } from "./EntityRow.js";
import { ShowInViewsControl } from "./ShowInViewsControl.js";

/** Organize → Calendars: drag-reorderable list (persisted `calendarOrder`).
 *  Every control — color, default-for-new-tasks, global show/hide, Month/Today
 *  visibility, cascade-aware delete — is inline on the row; only Show-in
 *  collapses behind a chevron on mobile. */
export function CalendarsPanel() {
  const { t } = useI18n();
  const calendars = useCalendarsStore((s) => s.calendars);
  const add = useCalendarsStore((s) => s.add);
  const rename = useCalendarsStore((s) => s.rename);
  const setHidden = useCalendarsStore((s) => s.setHidden);
  const remove = useCalendarsStore((s) => s.remove);
  const themeConfig = useThemeStore((s) => s.config);
  const setEntityColor = useThemeStore((s) => s.setEntityColor);
  const setAutoColorCalendars = useThemeStore((s) => s.setAutoColorCalendars);
  const defaultCalendarId = useSettingsStore((s) => s.defaultCalendarId);
  const setDefaultCalendarId = useSettingsStore((s) => s.setDefaultCalendarId);
  const calendarOrder = useSettingsStore((s) => s.calendarOrder);
  const setCalendarOrder = useSettingsStore((s) => s.setCalendarOrder);
  const tasks = useTasksStore((s) => s.tasks);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Calendar | null>(null);
  const guard = useDragClickGuard();

  const ordered = orderByIds(calendars, calendarOrder, (calendar) => calendar.id);
  const victimCount = confirming ? tasks.filter((task) => task.calendar_id === confirming.id).length : 0;
  const victimNoun = victimCount === 1 ? t("common.task") : t("common.tasks");

  const resolvedHex = (calendar: Calendar): string =>
    themeConfig.entity.calendars?.[calendar.id]
    ?? (themeConfig.autoColorCalendars
      ? distributedCategoricalColor(themeConfig, CAL_PALETTE_OFFSET + calendars.findIndex((x) => x.id === calendar.id))
      : "#888888");

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-end gap-2">
        <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={themeConfig.autoColorCalendars}
            onChange={(event) => setAutoColorCalendars(event.target.checked)}
            className="cursor-pointer"
          />
          {t("theme.autoColorCalendars")}
        </label>
      </div>

      <Reorder.Group
        as="div"
        axis="y"
        values={ordered.map((calendar) => calendar.id)}
        onReorder={(next) => void setCalendarOrder(next as string[])}
      >
        {ordered.map((calendar, index) => (
          <EntityRow
            key={calendar.id}
            id={calendar.id}
            guard={guard}
            indexLabel={index + 1}
            swatchHex={resolvedHex(calendar)}
            colorTarget={{ kind: "entity", target: { kind: "calendar", id: calendar.id } }}
            colorOverridden={themeConfig.entity.calendars?.[calendar.id] !== undefined}
            onRevertColor={() => setEntityColor({ kind: "calendar", id: calendar.id }, null)}
            name={calendar.name}
            onRename={(name) => void rename(calendar.id, name)}
            midActions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant={defaultCalendarId === calendar.id ? "primary" : "outline"}
                  onClick={() => void setDefaultCalendarId(calendar.id)}
                  title={t("lists.makeDefault")}
                  aria-label={t("lists.makeDefault")}
                  aria-pressed={defaultCalendarId === calendar.id}
                >
                  <Star className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void setHidden(calendar.id, !calendar.hidden)}
                  title={calendar.hidden ? t("lists.hiddenTitle") : t("lists.shownTitle")}
                  aria-label={calendar.hidden ? t("lists.hiddenTitle") : t("lists.shownTitle")}
                  aria-pressed={!calendar.hidden}
                >
                  {calendar.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </>
            }
            expandableActions={
              calendar.hidden
                ? <span className="text-xs text-muted-foreground">{t("organize.globallyHidden")}</span>
                : <ShowInViewsControl kind="calendar" id={calendar.id} />
            }
            deleteAction={
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setConfirming(calendar)}
                disabled={calendars.length <= 1}
                title={calendars.length <= 1 ? t("lists.onlyCalendarTitle") : t("lists.deleteCalendarTitle")}
                aria-label={calendars.length <= 1 ? t("lists.onlyCalendarTitle") : t("lists.deleteCalendarTitle")}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            }
            expanded={expandedId === calendar.id}
            onToggleExpand={() => setExpandedId(expandedId === calendar.id ? null : calendar.id)}
          />
        ))}
      </Reorder.Group>

      <AddEntityRow placeholder={t("lists.newCalendar")} onAdd={async (name) => { await add(name); }} />
      <DeleteEntityDialog
        name={confirming?.name}
        body={victimCount > 0
          ? t("lists.deleteCalendarWithTasks", { count: victimCount, noun: victimNoun })
          : t("lists.deleteCalendarEmpty")}
        confirmLabel={victimCount > 0
          ? t("lists.deleteCalendarAndTasks", { count: victimCount, noun: victimNoun })
          : t("lists.deleteCalendarTitle")}
        onConfirm={() => {
          const calendar = confirming;
          setConfirming(null);
          if (calendar) void remove(calendar.id);
        }}
        onClose={() => setConfirming(null)}
      />
    </div>
  );
}
